import { type PropsOf } from '@blaze-runtime/engine-domain';

import { type CanvasElementDefinition } from '../../canvas-elements/element-definition';
import { registerElementDefinition } from '../../services/element-definitions.service';

import {
  getContainerDefaults,
  getContainerSlotDefaults,
  getIoConfig,
  getNavigationContainerPropsConfig,
  getSidebarContainerSlotPropsConfig,
} from './_base-container.element';
import {
  type BaseContainerDef,
  type BaseContainerPropsModel,
  CONTAINER_INITIAL_SLOT_ID,
  SIDEBAR_CONTAINER_DEFAULT_WIDTH,
} from './_container.models';
import { ContainerComponent } from './container.component';

export type NavSidebarContainerDef = BaseContainerDef<NavSidebarContainerPropsModel>;

// Extend the CanvasElementDefinitions type
declare module '@blaze-runtime/engine-domain' {
  interface CanvasElementDefinitions {
    navSidebarContainer: NavSidebarContainerDef;
  }
}

export function getNavSidebarContainerDefinition(): CanvasElementDefinition<
  'navSidebarContainer',
  'container'
> {
  return {
    type: 'navSidebarContainer',
    variantOf: 'container',
    componentType: ContainerComponent,
    isListed: false,
    name: 'Navigation Sidebar',
    width: { mode: 'value', fixed: false, value: SIDEBAR_CONTAINER_DEFAULT_WIDTH },
    height: { mode: 'full' },
    slots: {
      ids: [CONTAINER_INITIAL_SLOT_ID],
      [CONTAINER_INITIAL_SLOT_ID]: {
        children: [],
      },
    },
    ioConfig: getIoConfig(),
    propsConfig: getNavigationContainerPropsConfig('navSidebarContainer', {
      hasFullHeightOption: true,
    }),
    slotPropsConfig: getSidebarContainerSlotPropsConfig,
    defaults: getDefaults(),
    slotDefaults: getContainerSlotDefaults(),
    styleIncludePaths: ['style'],
    features: {
      topLevelContainer: true,
    },
    version: 1,
  };
}

registerElementDefinition(getNavSidebarContainerDefinition());

export type NavSidebarContainerPropsModel = BaseContainerPropsModel & {
  content: {
    isSticky?: boolean;
    isFullHeight?: boolean;
  };
};

function getDefaults(): PropsOf<'navSidebarContainer'> {
  return {
    ...getContainerDefaults(),
    content: {
      isSticky: true,
      isFullHeight: false,
    },
  };
}
